import React, { useCallback, useContext, useState } from "react";
import { Paper, Box, Tabs, Tab, Container, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faClipboardUser,
  faCommentMedical,
  faFileInvoiceDollar,
  faHandHoldingDollar,
  faHouseChimneyMedical,
  faMoneyBill1Wave,
  faSackDollar,
  faUserTie,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";

import { getRuta } from "../../../../helpers/Helpers";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { EstadoChip, PageHeader } from "../../../../components/base";
import EmployeeDetails from "./EmployeeDetails";
import Vacaciones from "./Vacaciones";
import Adelantos from "./Adelantos";
import Prestamos from "./Prestamos";
import Bonos from "./Bonos";
import Aguinaldo from "./Aguinaldo";
import Permisos from "./Permisos";
import Reposos from "./Reposos";
import Subsidios from "./Subsidios";
import ResumenEmpleado from "./ResumenEmpleado";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`empleado-tabpanel-${index}`}
      aria-labelledby={`empleado-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Cada pestaña lleva su propio índice: el id de la pestaña y el del panel se corresponden.
function a11yProps(index) {
  return {
    id: `empleado-tab-${index}`,
    "aria-controls": `empleado-tabpanel-${index}`,
  };
}

export const EmployeDetailsContainer = () => {
  const [value, setValue] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const { access } = useContext(DataContext);
  const puedeVerPrestamos = tienePermiso(access, "NOMINA VER");

  // Lo que ya cargó la pestaña "Empleado", para el encabezado y el resumen (sin pedir nada nuevo).
  const [empleado, setEmpleado] = useState(null);
  const [fallo, setFallo] = useState(false);
  const alCargarDatos = useCallback((datos) => {
    setEmpleado(datos);
    setFallo(false);
  }, []);
  const alFallarDatos = useCallback(() => setFallo(true), []);

  let ruta = getRuta();
  let navigate = useNavigate();
  const { id } = useParams();

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Button
        onClick={() => {
          navigate(`${ruta}/employees`);
        }}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 1 }}
      >
        Volver a empleados
      </Button>

      <PageHeader
        titulo={empleado?.fullName || "Detalle del empleado"}
        subtitulo={
          empleado?.puesto
            ? `${empleado.puesto} · Empleado #${id}`
            : `Empleado #${id}`
        }
        icono={<FontAwesomeIcon icon={faUserTie} />}
        acciones={
          empleado ? (
            empleado.isActive ? (
              <EstadoChip estado="ok" label="Activo" />
            ) : (
              <EstadoChip estado="neutro" label="Inactivo" />
            )
          ) : undefined
        }
      />

      <ResumenEmpleado empleado={empleado} fallo={fallo} />

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="Secciones del empleado"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<FontAwesomeIcon icon={faClipboardUser} />}
            iconPosition="start"
            label="Empleado"
            value={0}
            {...a11yProps(0)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faCalendarCheck} />}
            iconPosition="start"
            label="Vacaciones"
            value={1}
            {...a11yProps(1)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faUserXmark} />}
            iconPosition="start"
            label="Ausencia"
            value={2}
            {...a11yProps(2)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faCommentMedical} />}
            iconPosition="start"
            label="Reposos"
            value={3}
            {...a11yProps(3)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faHouseChimneyMedical} />}
            iconPosition="start"
            label="Subsidios"
            value={4}
            {...a11yProps(4)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faMoneyBill1Wave} />}
            iconPosition="start"
            label="Adelanto"
            value={5}
            {...a11yProps(5)}
          />

          {/* Se ve con NOMINA VER, el permiso de GetPrestamosByEmp. Lleva su propio valor (8) para
              no mover el de las demás pestañas, que se muestran siempre. */}
          {puedeVerPrestamos ? (
            <Tab
              disabled={!isActive}
              icon={<FontAwesomeIcon icon={faFileInvoiceDollar} />}
              iconPosition="start"
              label="Préstamos"
              value={8}
              {...a11yProps(8)}
            />
          ) : null}

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faSackDollar} />}
            iconPosition="start"
            label="Bonos"
            value={6}
            {...a11yProps(6)}
          />

          <Tab
            disabled={!isActive}
            icon={<FontAwesomeIcon icon={faHandHoldingDollar} />}
            iconPosition="start"
            label="Aguinaldo"
            value={7}
            {...a11yProps(7)}
          />
        </Tabs>

        <TabPanel value={value} index={0}>
          <EmployeeDetails
            id={id}
            onDatos={alCargarDatos}
            onFallo={alFallarDatos}
            isActive={isActive}
            setIsActive={setIsActive}
          />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <Vacaciones id={id} />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <Permisos id={id} />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <Reposos id={id} />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <Subsidios id={id} />
        </TabPanel>
        <TabPanel value={value} index={5}>
          <Adelantos id={id} />
        </TabPanel>
        <TabPanel value={value} index={6}>
          <Bonos id={id} />
        </TabPanel>
        <TabPanel value={value} index={7}>
          <Aguinaldo id={id} />
        </TabPanel>
        {puedeVerPrestamos ? (
          <TabPanel value={value} index={8}>
            <Prestamos id={id} />
          </TabPanel>
        ) : null}
      </Paper>
    </Container>
  );
};
